import { useEffect, useState } from "react";
import { AppData, ResturantData } from "@/types/globalTypes";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import toast from "react-hot-toast";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { Skeleton } from "@/components/ui/skeleton";
import ErrorMessage from "@/components/ErrorMessage";
import { rerenderLayout } from "@/store/RenderProtectionRoute.slice";
import WhatsAppNotifierApp from "@/components/Apps/WhatsAppNotifierApp";
import { useTranslation } from "react-i18next";
import LoyaltyPointsApp from "@/components/Apps/LoyaltyPointsApp";

function Apps() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const dispatch = useDispatch();
  const [apps, setApps] = useState<AppData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [isNeedRefetch, setIsNeedRefetch] = useState(false);
  const [isPending, setIsPending] = useState(false);

  const ResturantData = useSelector(
    (state: { resturant: { resturantData: ResturantData } }) =>
      state.resturant.resturantData,
  );

  const WithoutPlan = ResturantData?.subscription === null;

  const BASE_URL = import.meta.env.VITE_BASE_URL;
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );

  useEffect(() => {
    if (!token) return;

    const fetchApps = async () => {
      const url = `${BASE_URL}/api/dashboard/apps?locale=${lang}`;

      setIsLoading(true);
      setError(null);
      setIsNeedRefetch(false);

      try {
        const response = await axios.get(url, {
          headers: {
            Authorization: `${token}`,
          },
        });

        if (response.data.success) {
          setApps(response.data.apps);
        } else {
          const message =
            response.data.error || (t("apps.fetchFailed") as string);
          setError(message);
          toast.error(message);
        }
      } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
          const message =
            error.response?.data?.error || (t("apps.fetchFailed") as string);
          setError(message);
          toast.error(message);
        } else {
          setError(t("apps.unexpectedError") as string);
          toast.error(t("apps.unexpectedError") as string);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchApps();
  }, [BASE_URL, lang, token, t, isNeedRefetch]);

  const installApp = async (appName: string) => {

    const loadingToast = toast.loading(t("apps.installing") as string);

    setIsPending(true);

    try {
      const response = await axios.post(
        `${BASE_URL}/api/dashboard/apps/install?locale=${lang}`,
        {
          appName,
        },
        {
          headers: {
            Authorization: token,
          },
        },
      );

      if (response.data.success) {
        const message =
          response.data.message || (t("apps.installSuccess") as string);
        toast.success(message);
        setIsNeedRefetch(true);
        dispatch(rerenderLayout());
      } else {
        const message =
          response.data.message || (t("apps.installFailed") as string);
        toast.error(message);
      }
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.error(
          t("apps.unexpectedError") as string,
        );
        toast.error(message);
      } else {
        toast.error(t("apps.unexpectedError") as string);
      }
    } finally {
      toast.dismiss(loadingToast);
      setIsPending(false);
    }
  };

  const uninstallApp = async (appName: string) => {

    const loadingToast = toast.loading(t("apps.uninstalling") as string);
    setIsPending(true);

    try {
      const response = await axios.post(
        `${BASE_URL}/api/dashboard/apps/uninstall?locale=${lang}`,
        {
          appName,
        },
        {
          headers: {
            Authorization: token,
          },
        },
      );

      if (response.data.success) {
        const message =
          response.data.message || (t("apps.uninstallSuccess") as string);
        toast.success(message);
        setIsNeedRefetch(true);
        dispatch(rerenderLayout());
      } else {
        const message =
          response.data.message || (t("apps.uninstallFailed") as string);
        toast.error(message);
      }
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.error(
          t("apps.unexpectedError") as string,
        );
        toast.error(message);
      } else {
        toast.error(t("apps.unexpectedError") as string);
      }
    } finally {
      toast.dismiss(loadingToast);
      setIsPending(false);
    }
  };

  if (isLoading) {
    return (
      <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 8 }).map((_, index) => (
          <Skeleton
            key={index}
            className="bg-card h-[200px] rounded-lg border shadow-md dark:bg-slate-600 dark:bg-opacity-35"
          />
        ))}
      </div>
    );
  }

  if (error) return <ErrorMessage msg={error} />;

  return (
    <>
      <h1 className="text-3xl font-semibold">{t("apps.title")}</h1>
      {/* EMPTY MESSAGE */}
      {!apps.length && (
        <Alert className="mt-5 border-none bg-skyblue text-sidebar dark:border-none dark:bg-skyblue dark:text-sidebar">
          <AlertTitle className="text-center text-xl">
            {t("apps.noAppsFound")}
          </AlertTitle>
          <AlertDescription className="my-5 text-center font-semibold">
            {t("apps.noAppsMessage")}
          </AlertDescription>
        </Alert>
      )}

      {/* Cards */}
      <div className="mt-10 grid grid-cols-1 justify-center gap-2 md:grid-cols-2 md:justify-normal lg:grid-cols-3">
        {apps.map((app) =>
          // if app name is whatsapp-notifier Show Separate Component for whatsapp
          app.name === "whatsapp-notifier" ? (
            <WhatsAppNotifierApp
              app={app}
              key={app.name}
              installApp={installApp}
              uninstallApp={uninstallApp}
              isPending={isPending}
            />
          ) : app.name === "loyalty" ? (
            <LoyaltyPointsApp
              app={app}
              key={app.name}
              installApp={installApp}
              uninstallApp={uninstallApp}
              isPending={isPending}
            />
          ) : (
            <div
              key={app.name}
              className="whatsapp-card relative flex flex-col gap-5 overflow-hidden rounded-lg border bg-white p-5"
            >
              <div className="card-head flex items-center justify-between">
                {/* image */}
                <div className="w-14">
                  <img src={app.image} alt="app image" className="w-full" />
                </div>

                {/* badge */}
                {WithoutPlan && (app.name !== "cashier")  ? (
                  <span
                    className={`absolute top-[20px] w-[8rem] bg-sidebar py-1 text-center font-semibold text-skyblue ${
                      i18n.language === "ar"
                        ? "end-[-30px] rotate-[-45deg]"
                        : "end-[-30px] -rotate-[-45deg]"
                    }`}
                  >
                    {t("subscribtionSettings.pro")}
                  </span>
                ) : !app.available ? (
                  <span
                    className={`absolute top-[20px] w-[8rem] bg-[#ffc62a] py-1 text-center font-semibold text-[#907017] ${
                      i18n.language === "ar"
                        ? "end-[-30px] rotate-[-45deg]"
                        : "end-[-30px] -rotate-[-45deg]"
                    }`}
                  >
                    {t("apps.soon")}
                  </span>
                ) : app.installed ? (
                  <span
                    className={`absolute top-[20px] w-[8rem] bg-green-400 py-1 text-center font-semibold text-green-700 ${
                      i18n.language === "ar"
                        ? "end-[-30px] rotate-[-45deg]"
                        : "end-[-30px] -rotate-[-45deg]"
                    }`}
                  >
                    {t("apps.active")}
                  </span>
                ) : (
                  <span
                    className={`absolute top-[20px] w-[8rem] bg-gray-400 py-1 text-center font-semibold text-gray-600 ${
                      i18n.language === "ar"
                        ? "end-[-30px] rotate-[-45deg]"
                        : "end-[-30px] -rotate-[-45deg]"
                    }`}
                  >
                    {t("apps.inactive")}
                  </span>
                )}
              </div>

              <div className="font-bold text-gray-500">
                {i18n.language === "ar" ? app.titleAR : app.titleEN}
              </div>
              <p className="text-slate-500">
                {i18n.language === "ar" ? app.descriptionAR : app.descriptionEN}
              </p>

              {app.static || ( WithoutPlan && (app.name !== "cashier")) ? null : app.available ===
                false ? null : app.installed ? (
                <button
                  disabled={isPending}
                  onClick={() => uninstallApp(app.name)}
                  className="mt-1 self-end rounded-md bg-red-500 px-3 py-2 text-white disabled:cursor-not-allowed disabled:bg-gray-500 disabled:text-gray-300"
                >
                  {t("apps.uninstall")}
                </button>
              ) : (
                <button
                  disabled={isPending}
                  onClick={() => installApp(app.name)}
                  className="self-end rounded-md bg-green-500 px-3 py-2 text-white disabled:cursor-not-allowed disabled:bg-gray-500 disabled:text-gray-300"
                >
                  {t("apps.install")}
                </button>
              )}
            </div>
          ),
        )}
      </div>
    </>
  );
}

export default Apps;
